import { z } from 'zod';
import type { PseBank } from '@src/backend/src/common/data/banks';
import type { PaymentInformation, PaymentMethod } from '../types';

export type PaymentInformationWarnings = {
  name?: string;
  documentType?: string;
  documentNumber?: string;
  email?: string;
  phoneNumber?: string;
  cardNumber?: string;
  cardExpDate?: string;
  cardCVC?: string;
  puntosColombiaDocumentType?: string;
  puntosColombiaDocumentNumber?: string;
};

export type PayerDocumentType = 'cc' | 'ce' | 'nit' | 'ps' | undefined;

const DOCUMENT_NUMBER_RE = /^\d{6,20}$/;
const PASSPORT_NUMBER_RE = /^[a-zA-Z0-9]{4,20}$/;
const NEQUI_PHONE_RE = /^3\d{9}$/;
const CARD_NUMBER_FORMAT_RE = /^\d{4}-\d{4}-\d{4}-\d{3,4}$/;
const CARD_EXP_RE = /^(0[1-9]|1[0-2])\/\d{2}$/;

export function isValidDocumentNumber(documentNumber: string, documentType?: PayerDocumentType): boolean {
  if (documentType === 'ps') return PASSPORT_NUMBER_RE.test(documentNumber);
  return DOCUMENT_NUMBER_RE.test(documentNumber);
}

export function isValidPhone(phoneNumber: string): boolean {
  return NEQUI_PHONE_RE.test(phoneNumber);
}

export function isValidPayerEmail(email: string): boolean {
  return z.string().email().safeParse(email).success;
}

export function isValidCardNumberFormatted(cardNumber: string): boolean {
  return CARD_NUMBER_FORMAT_RE.test(cardNumber);
}

export function isValidCardExpDate(cardExpDate: string): boolean {
  return CARD_EXP_RE.test(cardExpDate);
}

export function isValidCardCvc(cardCVC: string): boolean {
  return /^\d{3,4}$/.test(cardCVC);
}

export function isValidPayerName(name: string, documentType: PayerDocumentType): boolean {
  if (!documentType) return false;
  const trimmed = name.trim();
  if (documentType === 'nit') return trimmed.length > 0;
  return trimmed.split(/\s+/).filter(Boolean).length > 1;
}

export type PaymentFormAnalysisInput = {
  paymentMethod: PaymentMethod | undefined;
  merchant: { cardFeeConfirmation?: boolean } | undefined;
  pseBank: PseBank | undefined;
  name: string;
  documentType: PayerDocumentType;
  documentNumber: string;
  email: string;
  phoneNumber: string;
  cardNumber: string;
  cardExpDate: string;
  cardCVC: string;
  installments: number;
  cardFeeConfirmation: boolean;
  requiresPuntosColombia: boolean;
  puntosColombiaDocumentType: Exclude<PayerDocumentType, 'ps'>;
  puntosColombiaDocumentNumber: string;
};

export type PaymentFormAnalysis = {
  paymentInformation: PaymentInformation;
  warnings: PaymentInformationWarnings;
};

const PUNTOS_COLOMBIA_DOC_NUMBER_RE = /^\d{5,20}$/;

export function isValidPuntosColombiaDocumentNumber(documentNumber: string): boolean {
  return PUNTOS_COLOMBIA_DOC_NUMBER_RE.test(documentNumber);
}

export function analyzePaymentForm(input: PaymentFormAnalysisInput): PaymentFormAnalysis {
  const {
    paymentMethod,
    merchant,
    pseBank,
    name,
    documentType,
    documentNumber,
    email,
    phoneNumber,
    cardNumber,
    cardExpDate,
    cardCVC,
    installments,
    cardFeeConfirmation,
    requiresPuntosColombia,
    puntosColombiaDocumentType,
    puntosColombiaDocumentNumber,
  } = input;

  const validDocumentNumber = isValidDocumentNumber(documentNumber, documentType);
  const validEmail = isValidPayerEmail(email);
  const validPayerName = isValidPayerName(name, documentType);
  const payerFieldsReady = !!documentType && validPayerName && validDocumentNumber && validEmail;

  const validPhone = isValidPhone(phoneNumber);
  const validCardNumber = isValidCardNumberFormatted(cardNumber);
  const validCardExp = isValidCardExpDate(cardExpDate);
  const validCvc = isValidCardCvc(cardCVC);
  const cardReady = validCvc && validCardExp && validCardNumber;

  const validPuntosColombiaDocumentNumber = isValidPuntosColombiaDocumentNumber(puntosColombiaDocumentNumber);
  const puntosColombiaFieldsReady = !!puntosColombiaDocumentType && validPuntosColombiaDocumentNumber;
  const puntosColombiaGateOk = !requiresPuntosColombia || puntosColombiaFieldsReady;

  const warnings: PaymentInformationWarnings = {};
  if (!!documentType && documentType !== 'nit' && name.trim().length > 0 && !validPayerName) {
    warnings.name = 'Ingresa nombre y apellido.';
  }
  if (documentNumber.length > 0 && !validDocumentNumber) {
    warnings.documentNumber =
      documentType === 'ps' ? 'Debe tener entre 4 y 20 letras o números.' : 'Debe tener entre 6 y 20 dígitos.';
  }
  if (email.length > 0 && !validEmail) {
    warnings.email = 'Ingresa un correo electrónico válido.';
  }
  if (phoneNumber.length > 0 && !validPhone) {
    warnings.phoneNumber = 'Ingresa un celular colombiano de 10 dígitos que inicie con 3.';
  }
  if (cardNumber.length > 0 && !validCardNumber) {
    warnings.cardNumber = 'Ingresa un número de tarjeta válido.';
  }
  if (cardExpDate.length > 0 && !validCardExp) {
    warnings.cardExpDate = 'Ingresa una fecha válida (MM/AA).';
  }
  if (cardCVC.length > 0 && !validCvc) {
    warnings.cardCVC = 'Ingresa un código de 3 o 4 dígitos.';
  }
  if (requiresPuntosColombia && puntosColombiaDocumentNumber.length > 0 && !validPuntosColombiaDocumentNumber) {
    warnings.puntosColombiaDocumentNumber = 'Debe tener entre 5 y 20 dígitos.';
  }

  const cardFeeOk = !merchant?.cardFeeConfirmation || cardFeeConfirmation;
  const trimmedName = name.trim();

  const basePayer = {
    name: trimmedName,
    documentType,
    documentNumber,
    email,
    okay: true as const,
    ...(requiresPuntosColombia && puntosColombiaFieldsReady
      ? { puntosColombiaDocumentType, puntosColombiaDocumentNumber }
      : {}),
  };

  let paymentInformation: PaymentInformation = { okay: false };
  const id = paymentMethod?.id;

  if (id === 'pse' && pseBank && payerFieldsReady && puntosColombiaGateOk) {
    paymentInformation = { ...basePayer, pseBank };
  } else if (id === 'nequiButton' && validPhone && payerFieldsReady && puntosColombiaGateOk) {
    paymentInformation = { ...basePayer, phoneNumber };
  } else if (id === 'card' && cardReady && payerFieldsReady && cardFeeOk && puntosColombiaGateOk) {
    paymentInformation = {
      ...basePayer,
      cardNumber: cardNumber.replace(/\D/g, ''),
      cardExpDate: cardExpDate.replace(/\D/g, ''),
      cardCVC,
      installments,
    };
  } else if (
    (id === 'breb' || id === 'bancolombiaButton' || id === 'cash') &&
    payerFieldsReady &&
    puntosColombiaGateOk
  ) {
    paymentInformation = { ...basePayer };
  }

  return { paymentInformation, warnings };
}
